/**
 * Portal invoices list (tracker item 45): newest first, with the property
 * filter (hidden for a single property), "Load more", and the address of the
 * property each invoice was for (none when it had no job or spanned several).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PortalInvoiceSummary, PortalPage } from '../../api/types';
import { usePortal, usePortalApi } from './PortalContext';
import { PropertyFilter, usePropertyFilter } from './PropertyFilter';
import { EmptyBlock, FailureBlock, LoadingBlock } from './PortalStates';
import { invoiceDate, invoiceMoney } from '../invoiceUi';
import { describePortalFailure, localToday, portalAddress, portalInvoiceStatusClass, portalInvoiceStatusLabel, propertyIdParam, PortalFailure } from '../../utils/portal';
import { secondaryButtonClass } from '../../components/formStyles';

const PAGE_SIZE = 20;

export function PortalInvoicesPage() {
  const { me, reportFailure } = usePortal();
  const api = usePortalApi();
  const [filter, setFilter] = usePropertyFilter(me.user.id, me.properties);
  const [rows, setRows] = useState<PortalInvoiceSummary[]>([]);
  const [page, setPage] = useState<PortalPage<PortalInvoiceSummary>['pagination'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [failure, setFailure] = useState<PortalFailure | null>(null);
  const requestId = useRef(0);
  const today = localToday();

  const load = useCallback(async (pageNo: number) => {
    const id = ++requestId.current;
    if (pageNo === 1) { setLoading(true); setRows([]); } else setMoreLoading(true);
    setFailure(null);
    try {
      const result = await api.listPortalInvoices({ propertyId: propertyIdParam(filter), page: pageNo, limit: PAGE_SIZE });
      if (id !== requestId.current) return;
      setRows((current) => (pageNo === 1 ? result.data : [...current, ...result.data]));
      setPage(result.pagination);
    } catch (error) {
      if (id !== requestId.current) return;
      const described = describePortalFailure(error, 'We could not load your invoices.');
      setFailure(described);
      reportFailure(described);
    } finally {
      if (id === requestId.current) { setLoading(false); setMoreLoading(false); }
    }
  }, [api, filter, reportFailure]);

  useEffect(() => { void load(1); }, [load]);

  const selected = me.properties.find((p) => p.id === filter);
  const hasMore = !!page && page.page < page.pages;

  return (
    <div>
      <h1 className="mb-3 text-lg font-bold text-gray-900">Invoices</h1>
      <PropertyFilter choices={me.properties} value={filter} onChange={setFilter} />
      {selected && <p className="mb-3 text-sm text-gray-600" data-testid="filter-note">Invoices for {portalAddress(selected)}</p>}

      {loading && <LoadingBlock label="Loading invoices" />}
      {failure && <FailureBlock failure={failure} onRetry={() => void load(page && rows.length ? page.page + 1 : 1)} />}
      {!loading && !failure && rows.length === 0 && (
        <EmptyBlock>{selected ? 'No invoices for this property yet.' : 'You have no invoices yet.'}</EmptyBlock>
      )}
      {rows.length > 0 && (
        <ul className="space-y-3" aria-label="Invoices">
          {rows.map((invoice) => {
            const label = portalInvoiceStatusLabel(invoice, today);
            return (
              <li key={invoice.id}>
                <Link
                  to={`/portal/invoices/${invoice.id}`}
                  className="block min-h-[44px] rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200 hover:ring-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-base font-semibold text-gray-900">Invoice {invoice.invoiceNumber}</span>
                    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${portalInvoiceStatusClass(label)}`}>{label}</span>
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    {invoiceDate(invoice.invoiceDate)} · due {invoiceDate(invoice.dueDate)}
                  </div>
                  {invoice.property && (
                    <div className="mt-1 break-words text-sm text-gray-700" data-testid="invoice-property">{portalAddress(invoice.property)}</div>
                  )}
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-gray-600">Total {invoiceMoney(invoice.total)}</span>
                    <span className="font-semibold text-gray-900">Balance {invoiceMoney(invoice.balanceDue)}</span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      {hasMore && !failure && (
        <button type="button" className={`${secondaryButtonClass} mt-4 w-full`} disabled={moreLoading} onClick={() => void load((page?.page ?? 1) + 1)}>
          {moreLoading ? 'Loading…' : 'Load more'}
        </button>
      )}
      <p className="mt-6">
        <Link to="/portal/statement" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">View statement</Link>
        {' '}
        <Link to="/portal/payment-methods" className="inline-flex min-h-[44px] items-center px-2 text-sm font-semibold text-blue-700 hover:underline">Payment methods</Link>
      </p>
    </div>
  );
}
